/**
 * ContextSummary — Context Card Foundation for the Connections Explorer.
 * Displays the official editorial context blurb for a node, or a clean
 * placeholder when none has been published. When the operator admin token
 * is present (sessionStorage `archive-admin-token`) an inline editor
 * appears — content is only ever written by a human editor, never
 * auto-generated.
 */
import React, { useEffect, useState } from "react";
import { PenLine, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { networkSetContextSummary } from "../../lib/api";

const wordCount = (t) => t.trim().split(/\s+/).filter(Boolean).length;

export const ContextSummary = ({ node }) => {
  const adminToken = typeof window !== "undefined"
    ? sessionStorage.getItem("archive-admin-token") : null;
  const [value, setValue] = useState(node.context_summary || null);
  const [text, setText] = useState(node.context_summary || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(node.context_summary || null);
    setText(node.context_summary || "");
    setEditing(false);
  }, [node.slug, node.context_summary]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await networkSetContextSummary(adminToken, node.slug, text);
      setValue(r.context_summary);
      setEditing(false);
      toast.success("Context summary saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="mt-2" data-testid={`context-summary-editor-${node.slug}`}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
                  maxLength={1200} autoFocus
                  placeholder="Official short description for the Connections Explorer (plain text, ~50–120 words)…"
                  data-testid="context-summary-textarea"
                  className="w-full bg-[#0d0c0a] border border-[#3A3630] px-2.5 py-2 text-[12px] text-[#E4DFD3] leading-relaxed placeholder:text-[#4A463F] focus:outline-none focus:border-[#A68F5E]" />
        <div className="flex items-center gap-2 mt-1">
          <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#5A554C]">
            {wordCount(text)} words · target 50–120
          </span>
          <button onClick={save} disabled={saving} data-testid="context-summary-save"
                  className="ml-auto inline-flex items-center gap-1 border border-[#A68F5E] text-[#A68F5E] px-2 py-1 font-mono text-[8px] uppercase tracking-[0.2em] disabled:opacity-50">
            {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save
          </button>
          <button onClick={() => { setEditing(false); setText(value || ""); }}
                  data-testid="context-summary-cancel"
                  className="inline-flex items-center gap-1 text-[#757065] px-2 py-1 font-mono text-[8px] uppercase tracking-[0.2em]">
            <X size={10} /> Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2">
      {value ? (
        <p className="text-[11.5px] text-[#C9C2B4] leading-relaxed"
           data-testid={`context-summary-${node.slug}`}>{value}</p>
      ) : (
        <p className="font-mono text-[9px] text-[#5A554C] italic"
           data-testid={`context-summary-placeholder-${node.slug}`}>
          No editorial summary has been published for this record.
        </p>
      )}
      {adminToken && (
        <button onClick={() => setEditing(true)}
                data-testid={`context-summary-edit-${node.slug}`}
                className="mt-1 inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.2em] text-[#A68F5E] hover:text-[#D92D20] transition-colors">
          <PenLine size={9} /> {value ? "Edit" : "Add"} context summary
        </button>
      )}
    </div>
  );
};

export default ContextSummary;
